/**
 * Gateway block guard — hard-coded URL guard that rejects any real payment gateway URL.
 * CLAUDE.md Security #2: "No real payment gateway connection."
 * Any code path that calls a real gateway must be caught at this boundary.
 */

/** Payment gateway domains that must never be called in demo-ware or the POC. */
const BLOCKED_DOMAINS = [
  "shift4.com",
  "cibc.com",
  "fac.com",
  "americanexpress.com",
  "stripe.com",
  "adyen.com",
  "braintreepayments.com",
  "paypal.com",
  "square.com",
  "worldpay.com",
] as const;

export type BlockedDomain = (typeof BLOCKED_DOMAINS)[number];

export interface GatewayCheckPass {
  blocked: false;
}

export interface GatewayCheckBlocked {
  blocked: true;
  domain: string;
}

export type GatewayCheckResult = GatewayCheckPass | GatewayCheckBlocked;

/**
 * Checks whether a URL targets a real payment gateway.
 * Returns `{ blocked: true, domain }` if the URL matches a blocked domain.
 * Throws synchronously if the URL is syntactically invalid.
 */
export function checkGatewayUrl(urlOrPath: string): GatewayCheckResult {
  // Handle relative paths — they can't target external gateways
  if (urlOrPath.startsWith("/") || urlOrPath.startsWith("./")) {
    return { blocked: false };
  }

  let hostname: string;
  try {
    hostname = new URL(urlOrPath).hostname.toLowerCase();
  } catch {
    // Non-URL strings (e.g. "mock-gateway") are safe
    return { blocked: false };
  }

  const matched = BLOCKED_DOMAINS.find(
    (d) => hostname === d || hostname.endsWith(`.${d}`),
  );

  if (matched) {
    return { blocked: true, domain: matched };
  }

  return { blocked: false };
}

/**
 * Throws an error if the URL targets a real payment gateway.
 * Use at any HTTP call boundary in payment-flow code.
 */
export function assertNotGateway(urlOrPath: string): void {
  const result = checkGatewayUrl(urlOrPath);
  if (result.blocked) {
    throw new Error(
      `[gateway-block-guard] Attempted call to real payment gateway "${result.domain}" is forbidden in demo-ware. ` +
        `Use the mock payment adapter instead.`,
    );
  }
}

/**
 * Generates a demo confirmation number in the form PEL-XXXXX-XXXXXX.
 * Not cryptographically strong — suitable for demo display only.
 */
export function generateConfirmationNumber(): string {
  const seg1 = Math.random().toString(36).slice(2, 7).toUpperCase();
  const seg2 = Math.random().toString(36).slice(2, 8).toUpperCase();
  return `PEL-${seg1}-${seg2}`;
}
